import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { toast } from "sonner";
import { Mail, ArrowRight } from "lucide-react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const LOGO_URL = "https://customer-assets.emergentagent.com/job_saleshustler/artifacts/lbev3bte_Gemini_Generated_Image_r450fmr450fmr450.png";

export default function LoginPage() {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await axios.post(`${API}/auth/login`, 
        { email },
        { withCredentials: true }
      );

      const data = response.data;
      toast.success(`Welcome, ${data.name}!`);
      
      // Small delay to ensure cookie is set before navigation
      await new Promise(resolve => setTimeout(resolve, 100));
      navigate('/dashboard', { state: { user: data } });
    } catch (error) {
      const message = error.response?.data?.detail || 'Login failed';
      if (message.includes("not authorized") || message.includes("not approved")) {
        toast.error("Your email is not on the approved list. Please contact your admin to get access.", { duration: 6000 });
      } else {
        toast.error(message);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4 hero-gradient">
      <Card className="w-full max-w-md glass-panel border-white/10" data-testid="login-card">
        <CardHeader className="text-center space-y-4">
          <div className="flex justify-center">
            <img src={LOGO_URL} alt="IntegrityGO" className="h-16 w-auto" />
          </div>
          <CardTitle className="text-2xl font-bold text-foreground">Welcome to 20x5</CardTitle>
          <CardDescription className="text-muted-foreground">
            Enter your email to access your sales tracker
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <form onSubmit={handleLogin} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="email" className="text-foreground">Email</Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  id="email"
                  type="email"
                  placeholder="you@example.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="pl-10 h-12 bg-background/50 border-white/10 focus:border-primary"
                  required
                  autoFocus
                  data-testid="email-input"
                />
              </div>
            </div>

            <Button
              type="submit"
              className="w-full h-12 text-base bg-primary text-primary-foreground hover:bg-primary/90 glow-cyan"
              disabled={loading || !email.trim()}
              data-testid="login-submit-btn"
            >
              {loading ? (
                "Signing in..."
              ) : (
                <>
                  Continue
                  <ArrowRight className="ml-2 h-4 w-4" />
                </>
              )}
            </Button>
          </form>

          <p className="text-center text-sm text-muted-foreground">
            Don't have access?{" "}
            <button
              type="button"
              onClick={() => toast.info("Please contact your admin (jason@integritygo.com) to get approved.", { duration: 6000 })}
              className="text-primary hover:underline"
            >
              Contact your admin
            </button>
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
